import React, { useState, useCallback, useEffect } from 'react';
import { useAuthStore } from '../store';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, Building2, RefreshCw } from 'lucide-react';
import { apiService } from '../services/api';

export function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const data = await apiService.getCaptcha();
      setCaptchaToken(data.captchaToken || '');
      setCaptchaImage(data.image || '');
      setCaptchaAnswer('');
    } catch (err) {
      console.error('Captcha yüklenemedi:', err);
      setError('Captcha yüklenemedi. Sayfayı yenileyin.');
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      const data = await apiService.login({
        username,
        password,
        captchaToken,
        captchaAnswer: captchaAnswer.trim()
      });

      if (data.success !== false && data.token) {
        const userData = {
          id: data.id || data.user?.id || '1',
          username: data.username || data.user?.username || username,
          email: data.email || data.user?.email || `${username}@example.com`,
          role: data.role || data.user?.role || 'user',
          permissions: data.permissions || data.user?.permissions || ['Ana Sayfa']
        };

        const authToken = data.token || null;
        localStorage.clear();
        sessionStorage.clear();

        if (authToken) {
          localStorage.setItem('auth_token', authToken);
        }

        useAuthStore.getState().login(userData);
        navigate('/dashboard');
      } else {
        setError(data.error || data.message || 'Kullanıcı adı veya şifre hatalı!');
        await loadCaptcha();
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err?.message || 'Giriş yapılırken bir hata oluştu!');
      await loadCaptcha();
    } finally {
      setLoading(false);
    }
  }, [username, password, captchaToken, captchaAnswer, loading, navigate, loadCaptcha]);

  return (
    <div className="min-h-screen overflow-y-auto relative">
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(/belediye.jpg?v=1.0&cb=1&nocache=1&force=1&refresh=1&cache=1&timestamp=1&version=2.0)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#f8f9fa'
        }}
      />
      <div className="fixed inset-0 bg-black/25 z-0" />

      <div className="relative z-10 flex justify-center px-4 py-6 sm:py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-4">
            <div className="flex items-center justify-center w-24 h-24 rounded-full bg-white shadow-lg mx-auto mb-3 border-4 border-blue-100">
              <img src="/logo.svg" alt="Logo" className="w-20 h-16 object-contain" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white drop-shadow-2xl tracking-wide">
              WorkPulse – İş Nabzı
            </h1>
            <p className="text-base sm:text-lg font-bold text-white drop-shadow-lg mt-1">
              Bağcılar Belediyesi
            </p>
            <p className="text-sm sm:text-base font-semibold text-white/95 drop-shadow">
              Bilgi İşlem Müdürlüğü
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-2xl p-5 sm:p-6 border border-gray-100">
            <div className="flex items-center justify-center mb-3">
              <div className="p-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full">
                <Building2 className="h-6 w-6 text-white" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-center text-gray-800 mb-4">
              Giriş Yap
            </h2>

            {error && (
              <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm text-center">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kullanıcı Adı
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Kullanıcı adınızı girin"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Şifre
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Şifrenizi girin"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Güvenlik Kodu (Captcha)
                </label>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-[52px] rounded-lg border border-gray-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                    {captchaLoading ? (
                      <span className="text-sm text-gray-500">Yükleniyor...</span>
                    ) : captchaImage ? (
                      <img src={captchaImage} alt="Captcha" className="h-[52px] w-full object-contain" />
                    ) : (
                      <span className="text-sm text-gray-500">Captcha yok</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={loadCaptcha}
                    disabled={captchaLoading}
                    className="p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    title="Yenile"
                  >
                    <RefreshCw className={`h-5 w-5 text-gray-600 ${captchaLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <input
                  type="text"
                  value={captchaAnswer}
                  onChange={e => setCaptchaAnswer(e.target.value.toUpperCase())}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent tracking-widest uppercase"
                  placeholder="Yukarıdaki kodu girin"
                  autoComplete="off"
                  maxLength={8}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !username.trim() || !password.trim() || !captchaAnswer.trim() || !captchaToken}
                className="w-full mt-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold text-base hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Giriş yapılıyor...
                  </div>
                ) : (
                  'Giriş Yap'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
